/**
 * ProficiencyFidelity.js — v6.32.180
 *
 * Public, no-auth transparency page that turns MOSAIC's WIDA fidelity
 * posture into a snapshot any parent, district auditor, or RFP reader
 * can verify in 30 seconds.
 *
 * Data source: GET /api/public/wida-fidelity (deterministic, read-only).
 * No edits, no content-engine calls, no PII.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, ShieldCheck, BookOpen, Headphones, Mic, Pencil,
  CheckCircle2, FileText, Layers, BarChart3, Eye, RefreshCw, Download,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODALITY_META = {
  listening: { Icon: Headphones, color: "#002FA7", label: "Listening" },
  speaking:  { Icon: Mic,        color: "#7C3AED", label: "Speaking"  },
  reading:   { Icon: BookOpen,   color: "#059669", label: "Reading"   },
  writing:   { Icon: Pencil,     color: "#D97706", label: "Writing"   },
};

function fmtPct(p) {
  if (p === null || p === undefined) return "—";
  if (p >= 100) return "100%";
  return `${Number(p).toFixed(1)}%`;
}

function fmtNum(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString("en-US");
}

function Stat({ label, value, sub, testId }) {
  return (
    <div className="bg-white border-2 border-black p-5" data-testid={testId}>
      <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">{label}</div>
      <div className="text-3xl font-bold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function ProficiencyFidelity() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/public/wida-fidelity`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load transparency data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#002FA7] mb-8"
          data-testid="proficiency-fidelity-back-link"
        >
          <ArrowLeft size={16} /> Back to Home
        </Link>

        {/* Hero */}
        <div className="bg-[#002FA7] text-white border-2 border-black p-8 sm:p-12 mb-8" data-testid="proficiency-fidelity-hero">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-white text-[#002FA7] flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={28} />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-blue-200 font-semibold mb-2">
                MOSAIC Framework — Transparency Report
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                Mosaic Assessment Quality, in numbers.
              </h1>
              <p className="text-base text-blue-100 max-w-3xl leading-relaxed">
                MOSAIC is an independent assessment framework. Item types and presentation
                patterns mirror the WIDA ACCESS test (Rule 1b/1c). Scoring uses MOSAIC's
                own 6-level ladder. This page is regenerated live from our content database
                on every request — no marketing copy, just counts.
              </p>
              {data && (
                <div className="mt-4 text-xs text-blue-200 flex items-center gap-3 flex-wrap">
                  <span>Version <span className="font-semibold text-white" data-testid="proficiency-fidelity-version">{data.version}</span></span>
                  <span>•</span>
                  <span>Generated <span className="font-semibold text-white">{new Date(data.generated_at).toLocaleString()}</span></span>
                  <button
                    type="button"
                    onClick={load}
                    className="inline-flex items-center gap-1 px-2 py-0.5 border border-blue-300 hover:bg-blue-800/40 text-blue-100 font-semibold ml-2"
                    data-testid="proficiency-fidelity-refresh-btn"
                  >
                    <RefreshCw size={11} /> Refresh
                  </button>
                  {/* v6.32.182 — Download the live snapshot as a 2-page PDF.
                      The PDF carries a verification QR pointing back at the
                      live JSON endpoint, so districts can attach a signed
                      snapshot to RFP responses and board packets. */}
                  <a
                    href={`${API}/public/wida-fidelity.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-0.5 border border-blue-300 bg-white text-[#002FA7] hover:bg-blue-50 font-semibold"
                    data-testid="proficiency-fidelity-download-pdf"
                  >
                    <Download size={11} /> Download PDF
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {loading && (
          <div className="bg-white border-2 border-dashed border-gray-300 p-12 text-center" data-testid="proficiency-fidelity-loading">
            <div className="loading-spinner mx-auto mb-3" />
            <div className="text-sm text-gray-500">Loading the live fidelity snapshot…</div>
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-50 border-2 border-red-300 p-6" data-testid="proficiency-fidelity-error">
            <div className="font-semibold text-red-700 mb-1">Could not load transparency data</div>
            <div className="text-sm text-red-600">{error}</div>
            <button
              type="button"
              onClick={load}
              className="mt-3 px-4 py-2 bg-red-600 text-white font-semibold hover:bg-red-700"
              data-testid="proficiency-fidelity-retry-btn"
            >
              Retry
            </button>
          </div>
        )}

        {data && !loading && !error && (
          <>
            {/* Headline stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="proficiency-fidelity-headline-stats">
              <Stat
                label="WIDA Rule 1b Compliance"
                value={fmtPct(data.rule_1b.compliance_pct)}
                sub={`${fmtNum(data.rule_1b.rows_audited - data.rule_1b.row_violations)} of ${fmtNum(data.rule_1b.rows_audited)} items`}
                testId="stat-rule-1b"
              />
              <Stat
                label="ELD Standard Coverage"
                value={fmtPct(data.eld_klu_coverage.eld_pct)}
                sub={`${fmtNum(data.eld_klu_coverage.with_eld_standard)} items tagged`}
                testId="stat-eld"
              />
              <Stat
                label="Key Language Use Coverage"
                value={fmtPct(data.eld_klu_coverage.klu_pct)}
                sub={`${fmtNum(data.eld_klu_coverage.with_key_language_use)} items tagged`}
                testId="stat-klu"
              />
              <Stat
                label="WCAG 2.1 AA"
                value={data.accessibility.axe_violations_critical === 0 ? "Pass" : "Action needed"}
                sub={`${data.accessibility.axe_violations} axe-core findings (${data.accessibility.axe_violations_critical} critical)`}
                testId="stat-wcag"
              />
            </div>

            {/* Item Bank Breakdown */}
            <section className="bg-white border-2 border-black p-6 sm:p-8 mb-8" data-testid="section-item-bank">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-[#002FA7] text-white flex items-center justify-center">
                  <Layers size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Item Bank — {fmtNum(data.items.total)} items
                  </h2>
                  <p className="text-xs text-gray-500">Balanced across the four language modalities and the K-12 grade bands.</p>
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                {Object.entries(data.items.by_modality).map(([key, count]) => {
                  const meta = MODALITY_META[key] || { Icon: BookOpen, color: "#002FA7", label: key };
                  const Icon = meta.Icon;
                  const pct = data.items.total ? (count / data.items.total * 100) : 0;
                  return (
                    <div
                      key={key}
                      className="border-2 border-gray-200 p-4"
                      data-testid={`modality-card-${key}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className="w-8 h-8 flex items-center justify-center text-white"
                          style={{ backgroundColor: meta.color }}
                        >
                          <Icon size={16} />
                        </div>
                        <div className="font-semibold text-sm">{meta.label}</div>
                      </div>
                      <div className="text-2xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {fmtNum(count)}
                      </div>
                      <div className="text-xs text-gray-500">{pct.toFixed(1)}% of bank</div>
                      <div className="h-1 bg-gray-100 mt-2">
                        <div className="h-full" style={{ width: `${pct}%`, backgroundColor: meta.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3">
                  Grade Band Distribution
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {Object.entries(data.items.by_grade_band).map(([band, count]) => (
                    <div key={band} className="bg-gray-50 border border-gray-200 p-3 text-center" data-testid={`grade-band-${band}`}>
                      <div className="text-xs font-semibold text-gray-500">{band}</div>
                      <div className="text-xl font-bold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {fmtNum(count)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Presentation Patterns */}
            <section className="bg-white border-2 border-black p-6 sm:p-8 mb-8" data-testid="section-presentation-patterns">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-emerald-600 text-white flex items-center justify-center">
                  <CheckCircle2 size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Item-Type Fidelity — {data.item_type_fidelity.principle}
                  </h2>
                  <p className="text-xs text-gray-500">
                    Every item in the bank mirrors a WIDA ACCESS presentation pattern by modality.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {Object.entries(data.item_type_fidelity.presentation_patterns).map(([key, desc]) => {
                  const meta = MODALITY_META[key] || { Icon: BookOpen, color: "#002FA7", label: key };
                  const Icon = meta.Icon;
                  return (
                    <div key={key} className="border-2 border-gray-200 p-4 flex gap-3" data-testid={`pattern-${key}`}>
                      <div
                        className="w-9 h-9 flex items-center justify-center text-white flex-shrink-0"
                        style={{ backgroundColor: meta.color }}
                      >
                        <Icon size={16} />
                      </div>
                      <div>
                        <div className="font-semibold text-sm">{meta.label}</div>
                        <div className="text-xs text-gray-600 mt-1 leading-relaxed">{desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Framework Independence */}
            <section className="bg-white border-2 border-black p-6 sm:p-8 mb-8" data-testid="section-framework-independence">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-violet-600 text-white flex items-center justify-center">
                  <BarChart3 size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Framework Independence
                  </h2>
                  <p className="text-xs text-gray-500">
                    MOSAIC is an independent IP-clean framework — item patterns honor WIDA,
                    scoring uses our own ladder.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="border-2 border-gray-200 p-4">
                  <div className="text-xs uppercase tracking-wide font-semibold text-gray-500 mb-1">
                    Item-Pattern Fidelity (Locked)
                  </div>
                  <div className="text-base font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {data.framework_independence.item_pattern_fidelity}
                  </div>
                </div>
                <div className="border-2 border-gray-200 p-4">
                  <div className="text-xs uppercase tracking-wide font-semibold text-gray-500 mb-1">
                    Scoring Ladder (MOSAIC)
                  </div>
                  <div className="text-base font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {data.framework_independence.scoring_ladder}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-4 leading-relaxed">
                {data.framework_independence.note}
              </p>
            </section>

            {/* Accessibility + ERMA */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
              <div className="bg-white border-2 border-black p-6" data-testid="section-accessibility">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-amber-600 text-white flex items-center justify-center">
                    <Eye size={20} />
                  </div>
                  <h3 className="text-lg font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Accessibility — {data.accessibility.standard}
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-gray-50 border border-gray-200 p-3">
                    <div className="text-xs text-gray-500 font-semibold">axe-core findings</div>
                    <div className="text-2xl font-bold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                      {data.accessibility.axe_violations}
                    </div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 p-3">
                    <div className="text-xs text-gray-500 font-semibold">Critical</div>
                    <div className="text-2xl font-bold mt-1 text-emerald-700" style={{ fontFamily: "Outfit, sans-serif" }}>
                      {data.accessibility.axe_violations_critical}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-600 leading-relaxed">
                  Last audited: <span className="font-semibold">{data.accessibility.last_audited}</span>.{" "}
                  {data.accessibility.note}
                </div>
              </div>

              <div className="bg-white border-2 border-black p-6" data-testid="section-erma">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-rose-700 text-white flex items-center justify-center">
                    <FileText size={20} />
                  </div>
                  <h3 className="text-lg font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
                    ERMA Runbooks
                  </h3>
                </div>
                {data.erma_runbooks.length === 0 ? (
                  <p className="text-sm text-gray-500">No runbooks published yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.erma_runbooks.map((r) => (
                      <li
                        key={r.path}
                        className="border border-gray-200 p-3 text-sm flex items-center gap-2"
                        data-testid={`erma-runbook-${r.path}`}
                      >
                        <CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0" />
                        <span className="font-semibold">{r.title}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs text-gray-500 mt-3 leading-relaxed">
                  Runbooks are reviewed quarterly under NYS Education Law § 2-d and the
                  NYCPS Parents' Bill of Rights. Contact your district administrator for the
                  full text.
                </p>
              </div>
            </section>

            {/* Footer note */}
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 p-5 text-xs text-gray-600 leading-relaxed" data-testid="proficiency-fidelity-disclaimer">
              <p>
                <strong>About this page.</strong> Numbers are read directly from the live
                MOSAIC content database at request time. No caching, no manual entry.
                WIDA® is a registered trademark of the Board of Regents of the University of
                Wisconsin System; MOSAIC is independently developed by Lerno Co. and is not
                endorsed by WIDA.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
